import Vue from "vue";
import * as XLSX from "xlsx";
import { DetailFormComponentsEnum } from "./enum/detail-form-components.enum";
import { IFilterableClass, PaginationFilterListElement } from "./filterable";
import { flattenObject } from "./flatten-object";
import { IFormableClass, IMDetailFormConfig } from "./formable";
import { IExcelImportConfig } from "./interfaces/excel-import.interface";
import { $t } from "./utility/t";
/**
 *
 * @param entity
 * @returns
 */
export function getTranslationsFromEntity(entity: IFormableClass | IFilterableClass) {
  const formables = (entity as IFormableClass).formables;
  if (formables?.length) {
    return getTranslationsFromFormable(formables);
  }

  const filterables = (entity as IFilterableClass).filterables;
  if (filterables?.length) {
    return getTranslationsFromFilterable(filterables);
  }

  return new Map();
}

export function getTranslationsFromFormable(formables: IMDetailFormConfig<any>[]) {
  return new Map(
    formables.map((formable) => [formable.key, formable.props?.label ? $t(formable.props.label) : formable.key])
  );
}

export function getTranslationsFromFilterable(filterables: PaginationFilterListElement[]) {
  return new Map(
    filterables.map((formable) => [formable.key, formable.displayName ? $t(formable.displayName) : formable.key])
  );
}

/**
 * something like vehicle.contract.0.some.stuff.2.yup will become $t(vehicle.contract.some.stuff) [0] [2]
 */
export function transformKeyToColumnName(key: string, translations: Map<string, string>, addKey?: boolean) {
  // find all numbers in the key e.g. vehicle.contract.0.some.stuff.2.yup will return [0, 2]
  const matches = key.matchAll(RegExp(/\d/, "g"));
  // create a string with the indexes e.g. [0] [2]
  const indexes =
    Array.from(matches)
      .map((d) => `[${d}]`)
      .join("") || undefined;
  // remove the index numbers
  const clearedString = key.replaceAll(RegExp(/.\d/, "g"), "");

  if (!translations.get(clearedString)) {
    return key;
  }

  const translatedClearedString = translations.get(clearedString);
  const translatedClearedStringWithIndex = [translatedClearedString, indexes].join(" ");

  if (addKey) {
    return `${translatedClearedStringWithIndex} (${key})`;
  }

  return `${translatedClearedStringWithIndex}`;
}

/**
 * Excel cannot store more than 16384 columns (XFD) per sheet.
 *
 * @see https://support.microsoft.com/office/excel-specifications-and-limits-1672b34d-7043-467e-8e27-269d656771c3
 */
export const MAX_EXCEL_COLUMNS = 16384;

/**
 * Excel cannot store more than 1048576 rows per sheet. The header row counts towards the limit.
 */
export const MAX_EXCEL_ROWS = 1048576;

/**
 * Thrown when the data to export does not fit into a single excel sheet.
 *
 * Without this the sheet is written anyway and the writer silently drops everything beyond the
 * excel limits, so the user ends up with an incomplete file without ever being told.
 */
export class XlsxSheetLimitError extends Error {
  constructor(
    readonly columns: number,
    readonly rows: number
  ) {
    super(
      `The export does not fit into an excel sheet: ${columns} columns (max ${MAX_EXCEL_COLUMNS}), ` +
        `${rows} rows (max ${MAX_EXCEL_ROWS}).`
    );
    this.name = "XlsxSheetLimitError";
    Object.setPrototypeOf(this, XlsxSheetLimitError.prototype);
  }
}

/**
 * Show a failed excel export to the user.
 *
 * @see handleError only toasts `HttpException`s, but an export fails while the file is built in the
 * browser, so without this the user clicks download and nothing happens at all.
 */
export function handleDownloadError(error: unknown) {
  Vue.$log.error(error);

  if (error instanceof XlsxSheetLimitError) {
    // either limit can be the one that was hit, and naming the wrong one sends the user looking for
    // columns they do not have
    Vue.$toast.error(
      error.columns > MAX_EXCEL_COLUMNS
        ? $t("components.utility.PaginatedTable.downloadTooManyColumnsError", {
            columns: error.columns,
            maxColumns: MAX_EXCEL_COLUMNS
          })
        : $t("components.utility.PaginatedTable.downloadTooManyRowsError", {
            rows: error.rows,
            maxRows: MAX_EXCEL_ROWS
          })
    );

    return;
  }

  Vue.$toast.error($t("components.utility.PaginatedTable.downloadError"));
}

/**
 * A sheet as an array of arrays: the translated column names and one array of values per row.
 *
 * Values are indexed like @see IXlsxSheetData.header. A row only holds the values it actually has,
 * missing values stay empty instead of being materialized as empty strings.
 */
export interface IXlsxSheetData {
  /**
   * The translated column names, in column order.
   */
  header: string[];

  /**
   * One entry per document, holding its values indexed like @see IXlsxSheetData.header.
   */
  rows: any[][];
}

/**
 * Create the sheet data for a xlsx export: one column per unique (flattened) key of the documents
 * and one row per document.
 *
 * The rows are sparse on purpose. Filling every row with every column blows up to
 * `documents * columns` values, which for a wide export (a document with many line items or a
 * nested report adds thousands of columns) reaches millions of values and makes the browser throw
 * `RangeError: Too many properties to enumerate` before any file is written.
 *
 * @param docs the documents to be exported
 * @param headerTranslations map of key value pairs for the properties key to a translated result
 * @param sort should the keys(columns) be ordered alphabetically. Defaults to true
 * @param addKey configures the download to contain the exported properties keys in brackets in the header
 * @returns the translated header and the rows of values aligned to it
 */
export function createXlsxSheetData(
  docs: Record<string, any>[],
  headerTranslations?: Map<string, string>,
  sort = true,
  addKey = true
): IXlsxSheetData {
  const listOfFlattenedElements = docs.map((element) => flattenObject(element));

  let uniqueKeys: string[] = Array.from(new Set(listOfFlattenedElements.map((e) => Object.keys(e)).flat()));

  if (sort) {
    uniqueKeys = uniqueKeys.sort((a: string, b: string) => (a < b ? -1 : 1));
  }

  const header: string[] = [];
  /**
   * Column index per flattened key. Keys that translate to the same column name share a column,
   * the value of the last of those keys wins.
   */
  const columnIndexByKey = new Map<string, number>();
  const columnIndexByName = new Map<string, number>();

  for (const key of uniqueKeys) {
    const name = headerTranslations ? transformKeyToColumnName(key, headerTranslations, addKey) : key;

    let columnIndex = columnIndexByName.get(name);
    if (columnIndex === undefined) {
      columnIndex = header.length;
      header.push(name);
      columnIndexByName.set(name, columnIndex);
    }

    columnIndexByKey.set(key, columnIndex);
  }

  const rows = listOfFlattenedElements.map((element) => {
    const row: any[] = [];

    for (const key of Object.keys(element)) {
      const columnIndex = columnIndexByKey.get(key);
      if (columnIndex === undefined) {
        continue;
      }

      row[columnIndex] = element[key];
    }

    return row;
  });

  return { header, rows };
}

export function createXlsxObjectSimple(docs: Record<string, any>[], headerTranslations?: Map<string, string>) {
  const exportData: Record<string, any>[] = [];

  if (!headerTranslations) {
    throw new Error("headerTranslations is required for simple export");
  }

  for (const doc of docs) {
    const row: Record<string, any> = {};
    for (const [key, translated] of headerTranslations.entries()) {
      row[translated] = doc[key] ?? "";
    }

    exportData.push(row);
  }

  return exportData;
}

export function simpleExcelDownload(docs: Record<string, any>[], headerTranslations?: Map<string, string>) {
  const exportData = createXlsxObjectSimple(docs, headerTranslations);

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "exp");
  XLSX.writeFile(workbook, "export_" + new Date().toISOString() + ".xlsx", { compression: true });
}

/**
 * Download the given data as a xslx file
 * @see createXlsxSheetData
 *
 * @param docs
 * @param headerTranslations
 * @param sort should the keys(columns) be ordered alphabetically. Defaults to true
 * @param addKey configures the download to contain the exported properties keys in brackets in the header
 * @param title
 * @param isDense store the cells of the sheet in an array per row instead of one property per cell
 * @throws XlsxSheetLimitError if the data does not fit into a single excel sheet
 */
export function downloadAsXlsx(
  docs: Record<string, any>[],
  headerTranslations?: Map<string, string>,
  sort?: boolean,
  addKey?: boolean,
  title: string = "exp_" + new Date().toISOString(),
  isDense = false
) {
  const { header, rows } = createXlsxSheetData(docs, headerTranslations, sort, addKey);

  if (header.length > MAX_EXCEL_COLUMNS || rows.length + 1 > MAX_EXCEL_ROWS) {
    throw new XlsxSheetLimitError(header.length, rows.length + 1);
  }

  // aoa instead of json_to_sheet: json_to_sheet looks the column of every single value up in the
  // header by name, which is what makes a wide export take minutes instead of seconds.
  //
  // A sparse sheet - the library default - holds one property per cell on the worksheet object,
  // keyed by its address ("A1"). Writing one walks the whole rectangle of the sheet and looks every
  // address up, so it pays for the empty cells too. A dense sheet is indexed by row and column
  // directly: for 3000 rows x 4132 columns that is a 786ms write at 122MB against 14.9s at 414MB,
  // for the byte-identical file. It stays opt-in because a dense worksheet is a different shape
  // (`ws[r][c]`), so anything reading cells by address does not work on one.
  const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows], { dense: isDense });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "exp");
  XLSX.writeFile(workbook, title + ".xlsx", { compression: true });
}

/**
 * Download multiple sheets of the given data as a xslx file
 *
 * @param sheets
 * @param title
 */
export function downloadAsXlsxMultiple(
  sheets: {
    name: string;
    rows: Record<string, any>[];
    headerTranslations?: Map<string, string>;
  }[],
  title: string = "export_" + new Date().toISOString()
) {
  const workbook = XLSX.utils.book_new();

  for (const sheet of sheets) {
    if (!sheet.rows?.length) {
      continue;
    }

    const keys = Object.keys(sheet.rows[0]);
    const headers = keys.map((k) => sheet.headerTranslations?.get(k) ?? k);

    const data: Array<Array<any>> = [headers];

    for (const row of sheet.rows) {
      data.push(keys.map((k) => row[k] ?? ""));
    }

    const worksheet = XLSX.utils.aoa_to_sheet(data);

    XLSX.utils.book_append_sheet(workbook, worksheet, normalizeExcelName(sheet.name));
  }

  XLSX.writeFile(workbook, `${title}.xlsx`, { compression: true });
}

/**
 * Normalize excel name by replacing invalid characters with "_"
 * Set a limit of the name to 31 characters
 *
 * @param name
 * @returns
 */
function normalizeExcelName(name: string): string {
  return name.replace(/[:\\/?*\][[]/g, "_").substring(0, 31);
}

export function getExcelImportConfig(formConfig: IMDetailFormConfig[]) {
  let importConfig: { [key: string]: IExcelImportConfig } = {};
  const headerTranslations: Map<string, string> = new Map(
    formConfig.map((formConfigItem) => [formConfigItem.key, $t(formConfigItem.props?.label) ?? formConfigItem.key])
  );

  for (const formConfigItem of formConfig) {
    const identifer = transformKeyToColumnName(formConfigItem.key, headerTranslations);

    const config: IExcelImportConfig = {
      label: identifer,
      hint: formConfigItem.props.label ? $t(formConfigItem.props.hint) : "",
      required: formConfigItem.props.required ?? false,
      import: formConfigItem.props.required ?? false,
      originColumnNameInExcelFile: identifer,
      transform: (value: string | number | boolean) => {
        if (formConfigItem.props.rules) {
          const fails: string[] = [];
          for (const rule of formConfigItem.props.rules) {
            const fail = rule(value);
            if (fail !== true) fails.push(fail);
          }
          if (fails.length) {
            throw new Error(`${identifer}: ${fails.join(", ")}`);
          }
        }

        const items: string[] = [];
        for (const item of formConfigItem.props.items ?? []) {
          if (item?.value) {
            items.push(item.value);
          } else if (item) {
            items.push(item);
          }
        }
        if (
          [DetailFormComponentsEnum.SELECT_FIELD, DetailFormComponentsEnum.AUTO_COMPLETE].includes(
            formConfigItem.type
          ) &&
          items.length
        ) {
          if (items.length) {
            const found = items.find((item: any) => item == value); // not ===, but == to accomodate string/number comparison
            if (found === undefined) {
              throw new Error(
                $t("sign.Sign.error.invalidType", {
                  type: identifer,
                  expectedType: Object.values(items).join(` ${$t("or")} `),
                  receivedType: value
                })
              );
            }
          }
        }

        if (formConfigItem.props?.type) {
          if (formConfigItem.props.type === "date") {
            if (!value) {
              return "";
            }

            // Excel counts days since 1900-01-01 for type date
            if (typeof value === "number") {
              const serial = value as number;
              const excelEpoch = new Date("1900-01-01T00:00:00.000Z"); // January 1, 1900
              const daysOffset = serial - 2; // Date is off by 2 days
              const date = new Date(excelEpoch.getTime() + daysOffset * 24 * 60 * 60 * 1000);

              return date.toISOString();
            }

            return new Date(value.toString()).toISOString();
          }

          if (formConfigItem.props.type === "number") {
            return Number(value.toString());
          }

          if (formConfigItem.props.type === "boolean") {
            return Boolean(value);
          }

          return value.toString();
        }

        return value.toString();
      }
    };

    importConfig = {
      ...importConfig,
      [formConfigItem.key]: config
    };
  }

  return importConfig;
}
